import React, { useState } from "react";
import { useTranslate, useOne, useCustom } from "@refinedev/core";
import { useSelect } from "@refinedev/antd";
import {
  Form,
  Select,
  DatePicker,
  Button,
  Table,
  Typography,
  Space,
  Flex,
  Spin,
  Alert,
  Tag,
  Divider,
  Tabs,
  Segmented,
  Popover,
} from "antd";
import { GlassCard } from "../../shared/components";
import { useTenantSettings } from "../../hooks/tenant";
import type { TimeRangePickerProps } from "antd";
import {
  SearchOutlined,
  DollarOutlined,
  AppstoreOutlined,
  ContainerOutlined,
  DownloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import {
  AccountRatingResponseDto,
  RatingItemDto,
  ProductSummaryDto,
} from "../../models/account-rating";
import { theme } from "antd";

const excelSheetName = (name: string) =>
  name.replace(/[:\\/?*[\]]/g, "-").slice(0, 31);

const sanitizeFilenameSegment = (value: string) =>
  value
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60) || "export";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { useToken } = theme;

type ViewMode = "orders" | "products";

export const AccountRating: React.FC = () => {
  const _t = useTranslate();
  const { token } = useToken();
  const [form] = Form.useForm();
  const [viewMode, setViewMode] = useState<ViewMode>("orders");
  const [queryParams, setQueryParams] = useState<{
    accountId: number | "all";
    startDate: string;
    endDate: string;
  } | null>(null);

  const isAllAccounts = queryParams?.accountId === "all";

  const tenantSettings = useTenantSettings();
  const pricePerCredit = tenantSettings?.pricePerCredit;
  const creditsAvailable =
    typeof pricePerCredit === "number" && pricePerCredit > 0;

  const [currency, setCurrency] = useState<"eur" | "credit">(() =>
    localStorage.getItem("account-rating-currency-preference") === "credit"
      ? "credit"
      : "eur",
  );
  const effectiveCurrency =
    currency === "credit" && creditsAvailable ? "credit" : "eur";

  const handleCurrencyChange = (value: "eur" | "credit") => {
    setCurrency(value);
    localStorage.setItem("account-rating-currency-preference", value);
  };

  const formatAmount = (value: number) => {
    if (effectiveCurrency === "credit") {
      const credits = Math.round(value / (pricePerCredit as number));
      return `${new Intl.NumberFormat("fr-FR").format(credits)} ${_t(
        "accountRating.currency.creditLabel",
        "Cr",
      )}`;
    }
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const rangePresets: TimeRangePickerProps["presets"] = [
    {
      label: _t("accountRating.presets.lastMonth", "Last Month"),
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
    {
      label: _t("accountRating.presets.last3Months", "Last 3 Months"),
      value: [
        dayjs().subtract(3, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
    {
      label: _t("accountRating.presets.last6Months", "Last 6 Months"),
      value: [
        dayjs().subtract(6, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
  ];

  const { selectProps: accountSelectProps } = useSelect({
    resource: "accounts",
    optionLabel: "name",
    optionValue: "id",
  });

  const {
    data,
    isLoading: queryIsLoading,
    error,
  } = useCustom<AccountRatingResponseDto>({
    url:
      "account-rating" +
      (queryParams && queryParams.accountId !== "all"
        ? `/${queryParams.accountId}`
        : ""),
    method: "get",
    queryOptions: {
      enabled: !!queryParams,
    },
    config: {
      query: queryParams
        ? {
            startDate: queryParams.startDate,
            endDate: queryParams.endDate,
          }
        : undefined,
    },
  });

  // Only show loading when query is actually enabled and loading
  const isLoading = !!queryParams && queryIsLoading;

  const handleSubmit = (values: {
    accountId: number | "all";
    dateRange: [Dayjs, Dayjs];
  }) => {
    if (!values.accountId || !values.dateRange) {
      return;
    }

    const [startDate, endDate] = values.dateRange;

    console.log(
      "Date range",
      startDate.startOf("day").toISOString(),
      endDate.endOf("day").toISOString(),
    );

    setQueryParams({
      accountId: values.accountId,
      startDate: startDate.startOf("day").toISOString(),
      endDate: endDate.endOf("day").toISOString(),
    });
  };

  // Transform data for orders view
  const getOrdersViewData = () => {
    if (!data?.data?.items) return [];

    // Group items by orderId
    const orderMap = new Map<
      number,
      {
        orderId: number;
        orderDate: string;
        accountName?: string;
        make?: string | null;
        model?: string | null;
        vin?: string | null;
        totalAmount: number;
        products: RatingItemDto[];
      }
    >();

    data.data.items.forEach((item) => {
      if (!orderMap.has(item.orderId)) {
        orderMap.set(item.orderId, {
          orderId: item.orderId,
          orderDate: item.orderDate,
          accountName: item.accountName,
          make: item.make,
          model: item.model,
          vin: item.vin,
          totalAmount: 0,
          products: [],
        });
      }
      const order = orderMap.get(item.orderId)!;
      order.products.push(item);
      order.totalAmount += item.totalAmount;
    });

    return Array.from(orderMap.values());
  };

  // Get products view data directly from productSummary
  const getProductsViewData = () => {
    return data?.data?.productSummary || [];
  };

  const formatExportDateTime = (iso: string) =>
    dayjs(iso).format("DD/MM/YYYY HH:mm");

  const handleExportExcel = () => {
    if (!data?.data) return;
    const payload = data.data;
    const wb = XLSX.utils.book_new();

    const empty = "";

    const exportAmount = (value: number) =>
      effectiveCurrency === "credit"
        ? Math.round(value / (pricePerCredit as number))
        : value;

    const accountHeader = isAllAccounts
      ? [_t("accountRating.export.columns.account", "Compte")]
      : [];

    if (viewMode === "orders") {
      const orders = getOrdersViewData();
      const header = [
        ...accountHeader,
        _t("accountRating.table.orderId", "Order ID"),
        _t("accountRating.table.orderDate", "Order Date"),
        _t("accountRating.table.make", "Make"),
        _t("accountRating.table.model", "Model"),
        _t("accountRating.table.vin", "VIN"),
        _t("accountRating.export.columns.orderTotal", "Order total"),
        _t("accountRating.export.columns.productId", "Product ID"),
        _t("accountRating.table.productName", "Product Name"),
        _t("accountRating.table.productCode", "Product Code"),
        _t("accountRating.table.unitPrice", "Unit Price"),
        _t("accountRating.export.columns.lineTotal", "Line total"),
      ];
      const rows: (string | number)[][] = [];
      orders.forEach((o) => {
        rows.push([
          ...(isAllAccounts ? [o.accountName ?? ""] : []),
          o.orderId,
          formatExportDateTime(o.orderDate),
          o.make ?? "",
          o.model ?? "",
          o.vin ?? "",
          exportAmount(o.totalAmount),
          empty,
          empty,
          empty,
          empty,
          empty,
        ]);
        o.products.forEach((p: RatingItemDto) => {
          rows.push([
            ...(isAllAccounts ? [empty] : []),
            empty,
            empty,
            empty,
            empty,
            empty,
            empty,
            p.productId,
            p.productName,
            p.productCode,
            exportAmount(p.unitPrice),
            exportAmount(p.totalAmount),
          ]);
        });
      });
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([header, ...rows]),
        excelSheetName(_t("accountRating.export.sheetOrders", "Orders")),
      );
    } else {
      const products = getProductsViewData();
      const header = [
        ...accountHeader,
        _t("accountRating.export.columns.productId", "Product ID"),
        _t("accountRating.table.productName", "Product Name"),
        _t("accountRating.table.productCode", "Product Code"),
        _t("accountRating.table.quantity", "Quantity"),
        _t("accountRating.export.columns.productTotal", "Product total"),
        _t("accountRating.table.orderId", "Order ID"),
        _t("accountRating.table.orderDate", "Order Date"),
        _t("accountRating.table.make", "Make"),
        _t("accountRating.table.model", "Model"),
        _t("accountRating.table.vin", "VIN"),
      ];
      const rows: (string | number)[][] = [];
      products.forEach((p: ProductSummaryDto) => {
        rows.push([
          ...(isAllAccounts ? [empty] : []),
          p.productId,
          p.productName,
          p.productCode,
          p.quantity,
          exportAmount(p.totalAmount),
          empty,
          empty,
          empty,
          empty,
          empty,
        ]);
        p.orders.forEach((ord) => {
          rows.push([
            ...(isAllAccounts ? [ord.accountName ?? ""] : []),
            empty,
            empty,
            empty,
            empty,
            empty,
            ord.orderId,
            formatExportDateTime(ord.orderDate),
            ord.make ?? "",
            ord.model ?? "",
            ord.vin ?? "",
          ]);
        });
      });
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([header, ...rows]),
        excelSheetName(_t("accountRating.export.sheetProducts", "Products")),
      );
    }

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const tabSegment = viewMode === "orders" ? "orders" : "products";
    const accountSegment = isAllAccounts
      ? "all-accounts"
      : sanitizeFilenameSegment(payload.accountName);
    const currencySegment = effectiveCurrency === "credit" ? "cr" : "eur";
    const file = `account-rating_${accountSegment}_${tabSegment}_${currencySegment}_${dayjs().format(
      "YYYY-MM-DD_HHmm",
    )}.xlsx`;
    saveAs(
      new Blob([out], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      file,
    );
  };

  const accountColumn = {
    title: _t("accountRating.table.account", "Compte"),
    dataIndex: "accountName",
    key: "accountName",
    width: 180,
    render: (value: string | null) => value || "-",
  };

  const ordersColumns = [
    ...(isAllAccounts ? [accountColumn] : []),
    {
      title: _t("accountRating.table.orderId", "Order ID"),
      dataIndex: "orderId",
      key: "orderId",
      width: 100,
    },
    {
      title: _t("accountRating.table.orderDate", "Order Date"),
      dataIndex: "orderDate",
      key: "orderDate",
      width: 180,
      render: (date: string) => dayjs(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: _t("accountRating.table.make", "Make"),
      dataIndex: "make",
      key: "make",
      width: 120,
      render: (value: string | null) => value || "-",
    },
    {
      title: _t("accountRating.table.model", "Model"),
      dataIndex: "model",
      key: "model",
      width: 120,
      render: (value: string | null) => value || "-",
    },
    {
      title: _t("accountRating.table.vin", "VIN"),
      dataIndex: "vin",
      key: "vin",
      width: 150,
      render: (value: string | null) => value || "-",
    },
    {
      title: _t("accountRating.table.totalAmount", "Total Amount"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 150,
      align: "right" as const,
      render: (amount: number) => <Text strong>{formatAmount(amount)}</Text>,
    },
  ];

  const productsColumns = [
    {
      title: _t("accountRating.table.productName", "Product Name"),
      dataIndex: "productName",
      key: "productName",
      width: 200,
    },
    {
      title: _t("accountRating.table.productCode", "Product Code"),
      dataIndex: "productCode",
      key: "productCode",
      width: 150,
    },
    {
      title: _t("accountRating.table.quantity", "Quantity"),
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "right" as const,
    },
    {
      title: _t("accountRating.table.totalAmount", "Total Amount"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 150,
      align: "right" as const,
      render: (amount: number) => <Text strong>{formatAmount(amount)}</Text>,
    },
  ];

  const productColumns = [
    {
      title: _t("accountRating.table.productName", "Product Name"),
      dataIndex: "productName",
      key: "productName",
      width: 200,
    },
    {
      title: _t("accountRating.table.productCode", "Product Code"),
      dataIndex: "productCode",
      key: "productCode",
      width: 150,
    },
    {
      title: _t("accountRating.table.unitPrice", "Unit Price"),
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 120,
      align: "right" as const,
      render: (price: number) => formatAmount(price),
    },
    {
      title: _t("accountRating.table.totalAmount", "Total Amount"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 150,
      align: "right" as const,
      render: (amount: number) => <Text strong>{formatAmount(amount)}</Text>,
    },
  ];

  const orderColumns = [
    ...(isAllAccounts ? [accountColumn] : []),
    {
      title: _t("accountRating.table.orderId", "Order ID"),
      dataIndex: "orderId",
      key: "orderId",
      width: 100,
    },
    {
      title: _t("accountRating.table.orderDate", "Order Date"),
      dataIndex: "orderDate",
      key: "orderDate",
      width: 180,
      render: (date: string) => dayjs(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: _t("accountRating.table.make", "Make"),
      dataIndex: "make",
      key: "make",
      width: 120,
      render: (value: string | null) => value || "-",
    },
    {
      title: _t("accountRating.table.model", "Model"),
      dataIndex: "model",
      key: "model",
      width: 120,
      render: (value: string | null) => value || "-",
    },
    {
      title: _t("accountRating.table.vin", "VIN"),
      dataIndex: "vin",
      key: "vin",
      width: 150,
      render: (value: string | null) => value || "-",
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Title level={3} className="mb-6">
        {_t("accountRating.title", "Account Rating")}
      </Title>

      <GlassCard className="shadow-lg">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            dateRange: [dayjs().subtract(30, "days"), dayjs()],
          }}
        >
          <Flex gap="middle" wrap="wrap" align="flex-end">
            <Form.Item
              name="accountId"
              label={_t("accountRating.form.account", "Account")}
              rules={[
                {
                  required: true,
                  message: _t(
                    "accountRating.form.accountRequired",
                    "Please select an account",
                  ),
                },
              ]}
              style={{ minWidth: 250 }}
            >
              <Select
                {...accountSelectProps}
                options={[
                  {
                    label: _t(
                      "accountRating.form.allAccounts",
                      "Tous les comptes",
                    ),
                    value: "all",
                  },
                  ...(accountSelectProps.options ?? []),
                ]}
                placeholder={_t(
                  "accountRating.form.selectAccount",
                  "Select an account",
                )}
                size="large"
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item
              name="dateRange"
              label={_t("accountRating.form.dateRange", "Date Range")}
              rules={[
                {
                  required: true,
                  message: _t(
                    "accountRating.form.dateRangeRequired",
                    "Please select a date range",
                  ),
                },
              ]}
            >
              <RangePicker
                size="large"
                format="DD/MM/YYYY"
                presets={rangePresets}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                icon={<SearchOutlined />}
                loading={isLoading}
              >
                {_t("accountRating.form.search", "Search")}
              </Button>
            </Form.Item>
          </Flex>
        </Form>
      </GlassCard>

      {error && (
        <Alert
          message={_t("accountRating.error.title", "Error")}
          description={_t(
            "accountRating.error.description",
            "Failed to load account rating data",
          )}
          type="error"
          showIcon
          className="mb-6"
        />
      )}

      {data?.data && (
        <>
          <style>{`
                        .account-rating-table-view .ant-table { background: transparent !important; }
                        .account-rating-table-view .ant-table-thead > tr > th { background: transparent !important; }
                        .account-rating-table-view .ant-table-tbody > tr > td { background: transparent !important; }
                    `}</style>
          <GlassCard className="account-rating-table-view shadow-lg mt-4">
            <Flex
              justify="space-between"
              align="center"
              gap="large"
              wrap="wrap"
              className="mb-4"
            >
              <Flex align="center" gap="small">
                <Text strong className="text-lg">
                  {_t("accountRating.summary.accountName", "Account")}:
                </Text>
                <Text className="text-lg">
                  {isAllAccounts
                    ? _t(
                        "accountRating.summary.allAccountsLabel",
                        "Tous les comptes",
                      )
                    : data.data.accountName}
                </Text>
              </Flex>
              <Divider type="vertical" size="large" />
              <Flex align="center" gap="small">
                <Text strong className="text-lg">
                  {_t("accountRating.summary.period", "Period")}:
                </Text>
                <Text className="text-lg">
                  {dayjs(data.data.period.startDate).format("DD/MM/YYYY")} -{" "}
                  {dayjs(data.data.period.endDate).format("DD/MM/YYYY")}
                </Text>
              </Flex>
              <Divider type="vertical" size="large" />
              <Flex align="center" gap="small">
                <Text strong className="text-lg">
                  {_t("accountRating.summary.orderCount", "Order Count")}:
                </Text>
                <Tag color="blue" className="text-lg">
                  {data.data.items.length}
                </Tag>
              </Flex>
              <Text strong className="text-lg">
                {_t("accountRating.summary.grandTotal", "Grand Total")}:
              </Text>
              <Tag color="blue" className="text-lg" icon={<DollarOutlined />}>
                {formatAmount(data.data.grandTotal)}
              </Tag>
            </Flex>
            <Tabs
              activeKey={viewMode}
              onChange={(key) => setViewMode(key as ViewMode)}
              tabBarExtraContent={{
                right: (
                  <Space className="max-sm:mt-2">
                    <Popover
                      trigger="click"
                      placement="bottomRight"
                      title={_t(
                        "accountRating.options.title",
                        "Options d'affichage",
                      )}
                      content={
                        <Flex
                          vertical
                          gap="small"
                          style={{ minWidth: 220, maxWidth: 260 }}
                        >
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            {_t(
                              "accountRating.options.currencyLabel",
                              "Devise d'affichage",
                            )}
                          </Text>
                          <Segmented
                            block
                            value={effectiveCurrency}
                            onChange={(v) =>
                              handleCurrencyChange(v as "eur" | "credit")
                            }
                            options={[
                              {
                                label: _t(
                                  "accountRating.currency.euroLabel",
                                  "€",
                                ),
                                value: "eur",
                              },
                              {
                                label: _t(
                                  "accountRating.currency.creditLabel",
                                  "Cr",
                                ),
                                value: "credit",
                                disabled: !creditsAvailable,
                              },
                            ]}
                          />
                          {!creditsAvailable && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {_t(
                                "accountRating.options.creditUnavailable",
                                "Conversion en crédits indisponible pour ce compte",
                              )}
                            </Text>
                          )}
                        </Flex>
                      }
                    >
                      <Button type="default" icon={<SettingOutlined />}>
                        {_t("accountRating.options.button", "Options")}
                      </Button>
                    </Popover>
                    <Button
                      type="default"
                      icon={<DownloadOutlined />}
                      onClick={handleExportExcel}
                    >
                      {_t("accountRating.export.button", "Export Excel")}
                    </Button>
                  </Space>
                ),
              }}
              items={[
                {
                  key: "orders",
                  label: (
                    <span>
                      <ContainerOutlined className="mr-2" />
                      {_t("accountRating.view.byOrders", "By Orders")}
                    </span>
                  ),
                  children: (
                    <Table
                      columns={ordersColumns}
                      dataSource={getOrdersViewData()}
                      rowKey="orderId"
                      pagination={{ pageSize: 10 }}
                      expandable={{
                        expandedRowRender: (record) => (
                          <Table
                            columns={productColumns}
                            dataSource={record.products}
                            rowKey={(item) =>
                              `${item.orderId}-${item.productId}`
                            }
                            pagination={false}
                            size="small"
                          />
                        ),
                        rowExpandable: (record) => record.products.length > 0,
                      }}
                      loading={isLoading}
                    />
                  ),
                },
                {
                  key: "products",
                  label: (
                    <span>
                      <AppstoreOutlined className="mr-2" />
                      {_t("accountRating.view.byProducts", "By Products")}
                    </span>
                  ),
                  children: (
                    <Table
                      columns={productsColumns}
                      dataSource={getProductsViewData()}
                      rowKey="productId"
                      pagination={{ pageSize: 10 }}
                      expandable={{
                        expandedRowRender: (record) => (
                          <Table
                            columns={orderColumns}
                            dataSource={record.orders}
                            rowKey={(item) =>
                              `${record.productId}-${item.orderId}`
                            }
                            pagination={false}
                            size="small"
                          />
                        ),
                        rowExpandable: (record) => record.orders.length > 0,
                      }}
                      loading={isLoading}
                    />
                  ),
                },
              ]}
            />
          </GlassCard>
        </>
      )}

      {!data?.data && !isLoading && queryParams && (
        <GlassCard className="shadow-lg">
          <Alert
            message={_t("accountRating.noData.title", "No Data")}
            description={_t(
              "accountRating.noData.description",
              "No rating data found for the selected account and period",
            )}
            type="info"
            showIcon
          />
        </GlassCard>
      )}
    </div>
  );
};
